import { useEffect, useRef, useState } from 'react';
import { NEXT_STEP_META, PAYMENT_METHODS } from '../../constants';
import { getNextStep } from '../../utils';
import s from '../../styles/dashboard.module.css';

// Walks an appointment through its next checklist step: optional comment,
// payment method, and required before/after photos.
export function NextStepModal({ appt, onClose, onConfirm }) {
  const next = getNextStep(appt);
  const [comment, setComment]       = useState('');
  const [paymentMethod, setPaymentMethod] = useState('');
  const [files, setFiles]           = useState([]);
  const [previews, setPreviews]     = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError]           = useState('');
  const fileRef = useRef(null);

  useEffect(() => {
    const urls = files.map(f => URL.createObjectURL(f));
    setPreviews(urls);
    return () => urls.forEach(u => URL.revokeObjectURL(u));
  }, [files]);

  if (!next) {
    return (
      <div className={s.qrOverlay} onClick={onClose}>
        <div className={s.nextStepPanel} onClick={e => e.stopPropagation()}>
          <div className={s.nextStepEyebrow}>All Done</div>
          <div className={s.nextStepPrompt}>This appointment has completed every step.</div>
          <div className={s.nextStepActions}>
            <button className={s.nextStepCancel} onClick={onClose}>Close</button>
          </div>
        </div>
      </div>
    );
  }

  const meta = NEXT_STEP_META[next.step.toLowerCase()]
    || { title: next.step, prompt: `Mark "${next.step}" as done?`, requiredPhotos: 0, phase: 'photo', confirmLabel: 'Confirm' };
  const required     = meta.requiredPhotos || 0;
  const enoughPhotos = files.length >= required;
  const isPayment    = next.step.toLowerCase() === 'payment';
  const canConfirm   = enoughPhotos && (!isPayment || !!paymentMethod) && !submitting;

  const handlePick = (e) => {
    const picked = Array.from(e.target.files || []);
    if (picked.length) setFiles(prev => [...prev, ...picked]);
    e.target.value = '';
  };
  const removeFile = (i) => setFiles(prev => prev.filter((_, idx) => idx !== i));

  const handleYes = async () => {
    if (!canConfirm) return;
    setSubmitting(true);
    setError('');
    try {
      await onConfirm({ appt, step: next.step, idx: next.idx, comment, files, phase: meta.phase, paymentMethod: isPayment ? paymentMethod : undefined });
      onClose();
    } catch (err) {
      setError(err.message || 'Something went wrong');
      setSubmitting(false);
    }
  };

  return (
    <div className={s.qrOverlay} onClick={submitting ? undefined : onClose}>
      <div className={s.nextStepPanel} onClick={e => e.stopPropagation()}>
        <div className={s.nextStepEyebrow}>{meta.title}</div>
        <div className={s.nextStepCar}>{appt.car}</div>
        <div className={s.nextStepPrompt}>{meta.prompt}</div>

        <textarea
          className={s.nextStepTextarea}
          placeholder="Add a comment (optional)…"
          value={comment}
          onChange={e => setComment(e.target.value)}
          rows={3}
        />

        {isPayment && (
          <select
            className={s.nextStepTextarea}
            value={paymentMethod}
            onChange={e => setPaymentMethod(e.target.value)}
            disabled={submitting}
            style={{ height: 40 }}
          >
            <option value="" disabled>Select payment method…</option>
            {PAYMENT_METHODS.map(m => (
              <option key={m} value={m}>{m}</option>
            ))}
          </select>
        )}

        {required > 0 && (
          <>
            <div className={s.nextStepPhotoHead}>
              <span className={s.nextStepPhotoLabel}>Photos · {meta.phase}</span>
              <span className={enoughPhotos ? s.nextStepPhotoOk : s.nextStepPhotoNeed}>
                {files.length} / {required} required
              </span>
            </div>
            <div className={s.nextStepThumbs}>
              {previews.map((url, i) => (
                <div key={i} className={s.nextStepThumbWrap}>
                  <img src={url} alt="" className={s.nextStepThumb} />
                  <button className={s.nextStepThumbX} onClick={() => removeFile(i)} disabled={submitting}>✕</button>
                </div>
              ))}
              <button className={s.nextStepAddPhoto} onClick={() => fileRef.current?.click()} disabled={submitting}>+ Add</button>
            </div>
            <input ref={fileRef} type="file" accept="image/*" multiple style={{ display: 'none' }} onChange={handlePick} />
          </>
        )}

        {error && <div className={s.nextStepError}>{error}</div>}

        <div className={s.nextStepActions}>
          <button className={s.nextStepCancel} onClick={onClose} disabled={submitting}>Cancel</button>
          <button className={s.nextStepConfirm} onClick={handleYes} disabled={!canConfirm}>
            {submitting ? 'Saving…' : (meta.confirmLabel || 'Yes')}
          </button>
        </div>
        {required > 0 && !enoughPhotos && (
          <div className={s.nextStepHint}>Add at least {required} photos to continue.</div>
        )}
        {isPayment && !paymentMethod && (
          <div className={s.nextStepHint}>Select a payment method to continue.</div>
        )}
      </div>
    </div>
  );
}
